import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import * as Styles from './_goldTransactionEarmark.module.scss';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDatepicker from '~components/atoms/LabelledDatepicker';
import ActionButton from '~components/atoms/ActionButton';
import { toast } from 'react-toastify';
import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { GoldTypeEnum, MetalTypeEnum, SilverTypeEnum, TransactionActionEnum } from '~utils/enums';
import { TDropdownOption } from '~types/components/labelledDropdown';
import { TTransactionListing } from '~types/feat/gold';
import Yup from '~yup';

type EarmarkModalContentProps = {
    transaction?: TTransactionListing;
    onClose: () => void;
    mode?: 'earmark' | 'edit';
};

const EarmarkModalContent: React.FC<EarmarkModalContentProps> = ({ transaction, onClose, mode = 'earmark' }) => {
    const dispatch = useAppDispatch();

    const projectFilters = useAppSelector(state => state.project.projectFilters);
    const inventoryList = useAppSelector(state => state.gold.allInventories);
    const serialNumberList = useAppSelector(state => state.gold.allSerialNumbers);
    const serialNumberFilterParams = useAppSelector(state => state.gold.params.serialNumberFilterParams);
    const createEarmarkConversionLoading = useAppSelector(state => state.gold.loadings.createEarmarkConversion);
    const createEarmarkConversionError = useAppSelector(state => state.gold.errors.createEarmarkConversion);
    const updateEarmarkDetailsLoading = useAppSelector(state => state.gold.loadings.updateEarmarkDetails);
    const updateEarmarkDetailsError = useAppSelector(state => state.gold.errors.updateEarmarkDetails);

    const transactionMetalType =
        transaction?.goldType
            ? MetalTypeEnum.Gold
            : transaction?.silverType
                ? MetalTypeEnum.Silver
                : undefined;

    const earmarkSchema = Yup.object().shape({
        projectName: mode === 'edit' ? Yup.string() : Yup.string().required('Select a Project'),
        earmarkEndDate: Yup.string().required('Select the End Date'),
    });

    useEffect(() => {
        dispatch(combinedActions.projectGetProjectFiltersAttempt());
    }, []);

    const projectOptions: TDropdownOption[] = projectFilters
        ?.filter(project =>
            project.metalType === transactionMetalType
        )
        .map(project => ({
            label:
                    `${project.projectName} (RM ${project.earmarkAmount}, ${project.earmarkEndDate} Years)`,

            value: project.projectId,
        })) || [];

    const productOptions: TDropdownOption[] = inventoryList
        .filter(item =>
            item.productId === transaction?.items?.[0]?.productId
        )
        .map(item => ({
            label: item.productName,
            value: item.productId,
        }));

    const formik = useFormik({
        initialValues: {
            projectId: 0,
            projectName: '',
            earmarkEndDate: '',
            productId: 0,
            productName: '',
            goldType: 0,
            availableQuantity: 'Please select product first',
            amount: '',
            serialNo: '',
            goldAmountMYR: '',
            totalNetValue: '',
        },

        validationSchema: earmarkSchema,
        onSubmit: (values) => {
            if (!transaction?.transactionId) return;

            if (mode === 'edit') {
                dispatch(combinedActions.goldUpdateEarmarkDetailsAttempt({
                    transactionId: transaction.transactionId,
                    earmarkEndDate: values.earmarkEndDate,
                }));
                return;
            }

            dispatch(combinedActions.goldCreateEarmarkConversionAttempt({
                transactionId: transaction.transactionId,
                earmarkEndDate: values.earmarkEndDate,
                projectId: values.projectId,
            }));
        },
    });

    const handleOnChangeQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange(event);
        const quantity = Number(event.target.value);
        const product = inventoryList.find(item => item.productId === formik.values.productId);

        if (!product || !quantity) {
            formik.setFieldValue('serialNo', '');
            return;
        }

        const isPaperProduct =
            product.goldType === GoldTypeEnum.PaperGold ||
            product.silverType === SilverTypeEnum.PaperSilver;

        if (isPaperProduct) {
            formik.setFieldValue(
                'serialNo',
                'N/A'
            );
            return;
        }

        const serialNumbers =
            serialNumberList
                .slice(0, quantity)
                .map(item => item.serialNo);

        formik.setFieldValue(
            'serialNo',
            serialNumbers.join(', ')
        );

    };

    useEffect(() => {
        if (!transaction) return;

        const item =
            transaction.items?.[0];

        if (!item) {
            return;
        }

        const selectedProject =
            projectFilters.find(
                project =>
                    project.projectName ===
                    transaction.projectName
            );

        formik.setValues(prev => ({
            ...prev,

            productId: item.productId,
            productName: item.productName,
            amount: item.amount.toString(),
            projectId: selectedProject?.projectId || 0,
            projectName:
                mode === 'edit'
                    ? transaction.projectName || ''
                    : '',
        }));

    }, [
        transaction,
        projectFilters,
        mode,
    ]);

    useEffect(() => {
        if (!createEarmarkConversionLoading && formik.isSubmitting) {
            if (createEarmarkConversionError) {
                toast.error(createEarmarkConversionError);
                return;
            }
            toast.success('Earmark conversion request submitted successfully');
            onClose();
        }
    }, [
        createEarmarkConversionLoading,
        createEarmarkConversionError,
    ]);

    useEffect(() => {
        if (!updateEarmarkDetailsLoading && formik.isSubmitting) {
            if (updateEarmarkDetailsError) {
                toast.error(updateEarmarkDetailsError);
                return;
            }
            toast.success('Earmark details updated successfully');
            onClose();
        }
    }, [
        updateEarmarkDetailsLoading,
        updateEarmarkDetailsError,
    ]);

    return (
        <form className={Styles['container']} onSubmit={formik.handleSubmit}>

            <LabelledDropdown
                inputTestId='transaction-convert-earmark-projectName-input'
                optionTestId='transaction-convert-earmark-projectName-options'
                id='projectName'
                name='projectName'
                label='Select Project :'
                options={projectOptions}
                value={formik.values.projectName}
                error={formik.touched.projectName ? formik.errors.projectName : undefined}
                onChange={(e) => formik.handleChange({ target: { name: 'projectName', value: e.target.value } })}
                onSelectOption={(option) => formik.setFieldValue('projectId', option.value)}
                onBlur={formik.handleBlur}
                noOptionsMessage='No Project found'
                autoComplete='off'
                optionSelected={!!projectOptions.find(option => option.label === formik.values.projectName)}
                disabled={mode === 'edit'}
            />

            <div className={Styles['form-container']}>
            <LabelledDropdown
                inputTestId='transaction-convert-earmark-productName-input'
                id='productName'
                name='productName'
                label='Product Name* :'
                options={productOptions}
                value={formik.values.productName}
                error={formik.touched.productName ? formik.errors.productName : undefined}
                onBlur={formik.handleBlur}
                noOptionsMessage='No product found'
                autoComplete='off'
                optionSelected={!!productOptions.find(option => option.value === formik.values.productId)}
                disabled
            />

            <LabelledInput
                data-testid='transaction-convert-earmark-quantity-input'
                id='amount'
                name='amount'
                label='Quantity* :'
                value={formik.values.amount}
                error={formik.touched.amount ? formik.errors.amount : undefined}
                onChange={handleOnChangeQuantity}
                onBlur={formik.handleBlur}
                disabled
            />

                <LabelledInput
                    id='amount'
                    name='amount'
                    label='Quantity* :'
                    value={formik.values.amount}
                    error={formik.touched.amount ? formik.errors.amount : undefined}
                    onChange={handleOnChangeQuantity}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledDatepicker
                    id='earmarkEndDate'
                    name='earmarkEndDate'
                    label='End Date* :'
                    selectedDate={formik.values.earmarkEndDate}
                    error={formik.touched.earmarkEndDate ? formik.errors.earmarkEndDate : undefined}
                    setSelectedDate={(date: string) => formik.setFieldValue('earmarkEndDate', dayjs(date).toISOString())}
                    onBlur={formik.handleBlur}
                    minDate={dayjs().add(1, 'day').toISOString()}
                />
            </div>

            {/* {endDateError && <p style={{ color: 'red' }}>{endDateError}</p>} */}
            <div className={Styles['button-container']}>
                <ActionButton
                    testId='transaction-convert-earmark-confirm-button'
                    label='Confirm'
                    type='submit'
                    disabled={mode === 'edit' ? updateEarmarkDetailsLoading : createEarmarkConversionLoading}
                />
            </div>
        </form>
    );
};

export default EarmarkModalContent;
